import {CSSProperties} from 'react';

import {LayoutMethod, LayoutType} from './types';
import {
    alignBottomOfElement,
    alignLeftCenterOfElement,
    alignLeftOfElement,
    alignRightOfElement,
    alignTopOfElement,
    alignTopVerticallyCenterOfElementWithinBoundary,
    hasSpaceAbove,
    hasSpaceBelow,
    hasSpaceOnTheRight,
    matchWidthOfElement,
    rectCenter,
} from './utils';

type Calculation = Record<
    LayoutMethod,
    (input: CalculationInput) => CalculationOutput
>;

interface CalculationInput {
    boundaryRect: DOMRect;
    containerInnerRect: DOMRect;
    containerOuterRect: DOMRect;
    elementRect: DOMRect;
    layoutOffset?: number;
    matchWidth?: boolean;
}

type CalculationOutput = {
    layoutType: LayoutType;
} & Partial<Pick<CSSProperties, 'height' | 'left' | 'top' | 'width'>>;

const calculation: Calculation = {
    bottom: ({containerOuterRect, elementRect, matchWidth}) => ({
        layoutType: 'bottom',
        left: alignLeftCenterOfElement(elementRect, containerOuterRect),
        top: alignBottomOfElement(elementRect, containerOuterRect),
        width: matchWidthOfElement(elementRect, matchWidth),
    }),

    'bottom-left-or-top-right': ({
        boundaryRect,
        containerInnerRect,
        containerOuterRect,
        elementRect,
        layoutOffset,
    }) => {
        /**
         * Will try to display the element at the bottom if possible
         * else, will be displayed at the top
         */

        const isLayoutBottom = hasSpaceBelow({
            boundaryRect,
            containerInnerRect,
            elementRect,
            layoutOffset,
        });

        const isLayoutRight = hasSpaceOnTheRight({
            boundaryRect,
            containerInnerRect,
            elementRect,
            layoutOffset,
        });

        const verticalLayout = isLayoutBottom ? 'bottom' : 'top';
        const horizontalLayout = isLayoutRight ? 'right' : 'left';

        return {
            layoutType: `${verticalLayout}-${horizontalLayout}`,
            left:
                horizontalLayout === 'left'
                    ? alignLeftOfElement(elementRect, containerOuterRect)
                    : alignRightOfElement(elementRect, containerOuterRect),
            top:
                verticalLayout === 'top'
                    ? alignTopOfElement(elementRect, containerOuterRect)
                    : alignBottomOfElement(elementRect, containerOuterRect),
        };
    },

    'bottom-or-top': ({
        boundaryRect,
        containerInnerRect,
        containerOuterRect,
        elementRect,
        layoutOffset,
        matchWidth,
    }) => {
        /**
         * Will try to display the element at the bottom if possible
         * else, will be displayed at the top
         */

        const isLayoutBottom = hasSpaceBelow({
            boundaryRect,
            containerInnerRect,
            elementRect,
            layoutOffset,
        });

        return {
            layoutType: isLayoutBottom ? 'bottom' : 'top',
            left: alignLeftCenterOfElement(elementRect, containerOuterRect),
            top: isLayoutBottom
                ? alignBottomOfElement(elementRect, containerOuterRect)
                : alignTopOfElement(elementRect, containerOuterRect),
            width: matchWidthOfElement(elementRect, matchWidth),
        };
    },

    corner: ({boundaryRect, containerOuterRect, elementRect}) => {
        /**
         * If the element is in the top left corner of the container
         * return a layout type of 'bottom-right'
         *
         * If the element is in the top right corner of the container
         * return a layout type of 'bottom-left'
         *
         * If the element is in the bottom right corner of the container
         * return a layout type of 'top-left'
         *
         * If the element is in the bottom left corner of the container
         * return a layout type of 'top-right'
         */

        const boundaryCenter = rectCenter(boundaryRect);
        const elementCenter = rectCenter(elementRect);

        const isTopHalfOfBoundary = elementCenter.y <= boundaryCenter.y;
        const isLeftHalfOfBoundary = elementCenter.x <= boundaryCenter.x;

        const verticalLayout = isTopHalfOfBoundary ? 'bottom' : 'top';
        const horizontalLayout = isLeftHalfOfBoundary ? 'right' : 'left';

        return {
            layoutType: `${verticalLayout}-${horizontalLayout}`,
            left:
                horizontalLayout === 'left'
                    ? alignLeftOfElement(elementRect, containerOuterRect)
                    : alignRightOfElement(elementRect, containerOuterRect),
            top:
                verticalLayout === 'top'
                    ? alignTopOfElement(elementRect, containerOuterRect)
                    : alignBottomOfElement(elementRect, containerOuterRect),
        };
    },

    left: ({
        boundaryRect,
        containerInnerRect,
        containerOuterRect,
        elementRect,
        layoutOffset,
    }) => ({
        layoutType: 'left',
        left: alignRightOfElement(elementRect, containerOuterRect) - 5,
        top: alignTopVerticallyCenterOfElementWithinBoundary({
            boundaryRect,
            containerInnerRect,
            containerOuterRect,
            elementRect,
            layoutOffset,
        }),
    }),

    'left-corner': ({boundaryRect, containerOuterRect, elementRect}) => {
        const boundaryCenter = rectCenter(boundaryRect);
        const elementCenter = rectCenter(elementRect);

        const isTopHalfOfBoundary = elementCenter.y <= boundaryCenter.y;
        const verticalLayout = isTopHalfOfBoundary ? 'bottom' : 'top';

        return {
            layoutType: `${verticalLayout}-right`,
            left: alignRightOfElement(elementRect, containerOuterRect),
            top:
                verticalLayout === 'top'
                    ? alignTopOfElement(elementRect, containerOuterRect)
                    : alignBottomOfElement(elementRect, containerOuterRect),
        };
    },

    modal: () => ({
        height: '100%',
        layoutType: 'modal',
        left: 0,
        top: 0,
    }),

    'right-corner': ({boundaryRect, containerOuterRect, elementRect}) => {
        const boundaryCenter = rectCenter(boundaryRect);
        const elementCenter = rectCenter(elementRect);

        const isTopHalfOfBoundary = elementCenter.y <= boundaryCenter.y;
        const verticalLayout = isTopHalfOfBoundary ? 'bottom' : 'top';

        return {
            layoutType: `${verticalLayout}-left`,
            left: alignLeftOfElement(elementRect, containerOuterRect),
            top:
                verticalLayout === 'top'
                    ? alignTopOfElement(elementRect, containerOuterRect)
                    : alignBottomOfElement(elementRect, containerOuterRect),
        };
    },

    'top-or-bottom': ({
        boundaryRect,
        containerInnerRect,
        containerOuterRect,
        elementRect,
        layoutOffset,
        matchWidth,
    }) => {
        /**
         * Will try to display the element at the top if possible
         * else, will be displayed at the bottom
         */

        const isLayoutTop = hasSpaceAbove({
            boundaryRect,
            containerInnerRect,
            elementRect,
            layoutOffset,
        });

        return {
            layoutType: isLayoutTop ? 'top' : 'bottom',
            left: alignLeftCenterOfElement(elementRect, containerOuterRect),
            top: isLayoutTop
                ? alignTopOfElement(elementRect, containerOuterRect)
                : alignBottomOfElement(elementRect, containerOuterRect),
            width: matchWidthOfElement(elementRect, matchWidth),
        };
    },
};

type SetLayoutTypeAndStyle = (input: {
    boundaryElement?: Element | null;
    containerInner: Element | null;
    containerOuter: Element | null;
    element?: Element | null;
    layoutMethod: LayoutMethod;
    layoutOffset?: number;
    matchWidth?: boolean;
}) => CalculationOutput | undefined;

const setLayoutTypeAndStyle: SetLayoutTypeAndStyle = ({
    boundaryElement,
    containerInner,
    containerOuter,
    element,
    layoutMethod,
    layoutOffset,
    matchWidth,
}) => {
    if (layoutMethod === 'modal') {
        return {
            height: '100%',
            layoutType: 'modal',
            left: 0,
            right: 0,
            top: 0,
        };
    }

    if (!element) {
        return;
    }

    const elementRect = element.getBoundingClientRect();

    const containerOuterRect =
        containerOuter?.getBoundingClientRect() || new DOMRect();

    const containerInnerRect =
        containerInner?.getBoundingClientRect() || new DOMRect();

    const boundaryRect = boundaryElement
        ? boundaryElement.getBoundingClientRect()
        : new DOMRect(0, 0, window.innerWidth, window.innerHeight);

    return calculation[layoutMethod]({
        boundaryRect,
        containerInnerRect,
        containerOuterRect,
        elementRect,
        layoutOffset,
        matchWidth,
    });
};

export default setLayoutTypeAndStyle;
